import React from 'react';
import { useNavigate } from 'react-router-dom';
import MatchSetsResult from './match/MatchSetsResult';
import { SetPlacar } from '../types/placar';
import { atletasDoLado, ladoVencedor, AtletaLado } from '../utils/atletasDoLado';

export interface MatchCardPartida {
  id: string;
  data?: string | null;
  status?: string;
  categoriaTorneio?: {
    id: string;
    nome: string;
    torneio: {
      id: string;
      nome: string;
      avatarUrl?: string | null;
    };
  };
  equipeA: {
    id: string;
    nome: string;
    atletas: {
      id: string;
      usuario: {
        id: string;
        nome: string;
        imagemUrl: string | null;
        username: string;
      };
    }[];
  } | null;
  equipeB: {
    id: string;
    nome: string;
    atletas: {
      id: string;
      usuario: {
        id: string;
        nome: string;
        imagemUrl: string | null;
        username: string;
      };
    }[];
  } | null;
  participantesA?: {
    id: string;
    usuario?: {
      id: string;
      nome?: string | null;
      imagemUrl?: string | null;
      username?: string | null;
    } | null;
  }[] | null;
  participantesB?: {
    id: string;
    usuario?: {
      id: string;
      nome?: string | null;
      imagemUrl?: string | null;
      username?: string | null;
    } | null;
  }[] | null;
  placar: {
    id?: string;
    resultadoFinalA?: number | null;
    resultadoFinalB?: number | null;
    sets: SetPlacar[];
  } | null;
  equipeAWO?: boolean | null;
  equipeBWO?: boolean | null;
  equipeADesistencia?: boolean | null;
  equipeBDesistencia?: boolean | null;
  equipeVencedora?: string | null;
}

interface MatchCardProps {
  partida: MatchCardPartida;
  showHeader?: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({ partida, showHeader }) => {
  const navigate = useNavigate();

  const torneio = partida.categoriaTorneio?.torneio;
  const categoria = partida.categoriaTorneio?.nome;

  // Atletas por lado: usa a equipe quando presente; senão cai para os
  // participantes avulsos (rodízio — equipeA/equipeB nulos).
  const atletasA = atletasDoLado(partida, 'A');
  const atletasB = atletasDoLado(partida, 'B');
  const showHeaderSection = showHeader !== false && torneio != null;

  // Determinar vencedor pelo lado "A"/"B" (fonte única — cobre rodízio e legado).
  const winner: 'A' | 'B' | null = ladoVencedor(partida);

  const renderTeam = (atletas: AtletaLado[], isWinner: boolean) => (
    <div className="d-flex flex-column gap-1">
      {atletas.map((atleta) => (
        <div key={atleta.id} className="d-flex align-items-center gap-2">
          <img
            src={atleta.imagemUrl || '/assets/images/user.png'}
            alt={atleta.nome}
            className="rounded-circle"
            style={{ width: 32, height: 32, objectFit: 'cover', flexShrink: 0 }}
          />
          <span
            className={`font-xsssss text-truncate ${isWinner ? 'fw-700 text-grey-900' : 'fw-500 text-grey-600'}`}
          >
            {atleta.nome}
          </span>
        </div>
      ))}
      {atletas.length === 0 && (
        <span className="font-xsssss text-muted">A definir</span>
      )}
    </div>
  );

  return (
    <div
      className="card shadow-xss rounded-xxl border-0 mb-0 hover-card"
      style={{ cursor: 'pointer', maxWidth: 480, width: '100%' }}
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/singlematch/${partida.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/singlematch/${partida.id}`);
        }
      }}
    >
      {/* Header: torneio + categoria */}
      {showHeaderSection && torneio && (
        <div className="card-body p-3 pb-0">
          <div className="d-flex align-items-center gap-2">
            <img
              src={torneio.avatarUrl || '/assets/images/avatar.jpg'}
              alt={torneio.nome}
              className="rounded-circle"
              style={{ width: 24, height: 24, objectFit: 'cover', flexShrink: 0 }}
            />
            <div className="min-w-0">
              <span className="font-xsssss fw-600 text-grey-700 text-truncate d-block">
                {torneio.nome}
              </span>
              <span className="font-xsssss text-grey-500 text-truncate d-block" style={{ marginTop: -2 }}>
                {categoria}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Body: equipeA | placar | equipeB */}
      <div className={`card-body p-3 ${showHeaderSection ? 'pt-2' : ''}`}>
        <div className="d-flex align-items-center">
          {/* Equipe A */}
          <div className="flex-grow-1 min-w-0" style={{ flexBasis: '40%' }}>
            {renderTeam(atletasA, winner === 'A')}
          </div>

          {/* Placar central */}
          <div
            className="text-center px-2"
            style={{ flexShrink: 0, maxHeight: 80, overflowY: 'auto' }}
          >
            <MatchSetsResult
              placar={partida.placar}
              equipeAWO={partida.equipeAWO}
              equipeBWO={partida.equipeBWO}
              equipeADesistencia={partida.equipeADesistencia}
              equipeBDesistencia={partida.equipeBDesistencia}
              className="font-xssss"
            />
          </div>

          {/* Equipe B */}
          <div className="flex-grow-1 min-w-0 text-end" style={{ flexBasis: '40%' }}>
            <div className="d-flex flex-column gap-1 align-items-end">
              {atletasB.map((atleta) => (
                <div key={atleta.id} className="d-flex align-items-center gap-2 flex-row-reverse">
                  <img
                    src={atleta.imagemUrl || '/assets/images/user.png'}
                    alt={atleta.nome}
                    className="rounded-circle"
                    style={{ width: 32, height: 32, objectFit: 'cover', flexShrink: 0 }}
                  />
                  <span
                    className={`font-xsssss text-truncate ${winner === 'B' ? 'fw-700 text-grey-900' : 'fw-500 text-grey-600'}`}
                  >
                    {atleta.nome}
                  </span>
                </div>
              ))}
              {atletasB.length === 0 && (
                <span className="font-xsssss text-muted">A definir</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchCard;
